import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { WorkflowExample } from '../../../constants/workflowExamples';

export type { WorkflowExample };

const LinkedInIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const AirbnbIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 448 512" width={size} height={size} fill="#FF5A5F">
    <path d="M224 373.12c-25.24-31.67-40.08-59.43-45-83.18-22.55-88 112.61-88 90.06 0-5.45 24.25-20.29 52-45 83.18zm138.15 73.23c-42.06 18.31-83.67-10.88-119.3-50.47 103.9-130.07 46.11-200-18.85-200-54.92 0-85.16 46.51-73.28 100.5 6.93 29.19 25.23 62.39 54.43 99.5-32.53 36.05-60.55 52.69-85.15 54.92-50 7.43-89.11-41.06-71.3-91.09 15.1-39.16 111.72-231.18 115.87-241.56 15.75-30.07 25.56-57.4 59.38-57.4 32.34 0 43.4 25.94 60.37 59.87 36 70.62 89.35 177.48 114.84 239.09 13.17 33.07-1.37 71.29-37.01 86.64zm47-136.12C280.27 35.93 273.13 32 224 32c-45.52 0-64.87 31.67-84.66 72.79C33.18 317.1 22.89 347.19 22 349.81-3.22 419.14 48.74 480 111.63 480c21.71 0 60.61-6.06 112.37-62.4 58.68 63.78 101.26 62.4 112.37 62.4 62.89 0 114.85-60.86 89.61-130.19 0-3.89-16.82-38.9-16.82-39.58z"/>
  </svg>
);

const GitHubIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="#171515">
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
  </svg>
);

const SalesforceIcon = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="#00A1E0">
    <path d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.16 5.22c-.345 0-.69-.045-1.02-.105a3.9 3.9 0 01-3.315 1.86c-.57 0-1.11-.12-1.605-.345A4.535 4.535 0 018.58 20.4c-1.68 0-3.165-.93-3.96-2.295A4.69 4.69 0 013.93 18.3C1.755 18.3 0 16.53 0 14.325s1.755-3.975 3.93-3.975c.24 0 .48.015.705.06a4.848 4.848 0 014.5-5.205 4.8 4.8 0 01.87.21z"/>
  </svg>
);

const BrandIcon: React.FC<{ icon: WorkflowExample['icon']; accentColor: string }> = ({ icon, accentColor }) => {
  const iconMap = {
    linkedin: <LinkedInIcon size={16} />,
    airbnb: <AirbnbIcon size={16} />,
    github: <GitHubIcon size={16} />,
    salesforce: <SalesforceIcon size={16} />,
    building: <Building2 className="w-4 h-4" style={{ color: accentColor }} />,
  };
  return (
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: `${accentColor}15` }}
    >
      {iconMap[icon]}
    </div>
  );
};

const DEFAULT_AUTO_ROTATE_MS = 5000;

interface WorkflowCarouselProps {
  examples: WorkflowExample[];
  onExampleClick: (example: WorkflowExample) => void;
  autoRotateInterval?: number;
  pauseAutoRotate?: boolean;
}

const STEP_CONFIG = [
  { label: 'Find', color: 'bg-blue-500' },
  { label: 'Enrich', color: 'bg-purple-500' },
  { label: 'Outreach', color: 'bg-green-500' },
];

export const WorkflowCarousel: React.FC<WorkflowCarouselProps> = ({
  examples,
  onExampleClick,
  autoRotateInterval = DEFAULT_AUTO_ROTATE_MS,
  pauseAutoRotate = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (autoRotateInterval <= 0 || examples.length === 0 || pauseAutoRotate) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const len = examples.length;
        return len > 0 ? (prev + 1) % len : 0;
      });
    }, autoRotateInterval);
    return () => clearInterval(interval);
  }, [examples, autoRotateInterval, pauseAutoRotate]);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? examples.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === examples.length - 1 ? 0 : prev + 1));
  };

  const currentExample = examples[currentIndex];

  if (!currentExample) return null;

  return (
    <div className="mt-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-px flex-1 bg-neutral-200/80" />
        <span className="text-[10px] text-neutral-400 uppercase tracking-wider">or try an example</span>
        <div className="h-px flex-1 bg-neutral-200/80" />
      </div>

      <Card
        padding="sm"
        hoverable
        onClick={() => onExampleClick(currentExample)}
        className="bg-gradient-to-br from-neutral-50/80 to-white active:bg-neutral-50 group sm:p-4"
      >
        <div className="flex items-start justify-between mb-2 sm:mb-3">
          <div className="flex items-center gap-2.5">
            <BrandIcon icon={currentExample.icon} accentColor={currentExample.accentColor} />
            <div>
              <h3
                key={`title-${currentIndex}`}
                className="text-[13px] sm:text-sm font-semibold text-neutral-900 animate-fadeIn"
              >
                {currentExample.title}
              </h3>
              <p
                key={`subtitle-${currentIndex}`}
                className="text-[10px] sm:text-[11px] text-neutral-500 animate-fadeIn"
              >
                {currentExample.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              aria-label="Previous example"
              className="p-1.5 text-neutral-400 hover:text-neutral-600"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              aria-label="Next example"
              className="p-1.5 text-neutral-400 hover:text-neutral-600"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div key={`steps-${currentIndex}`} className="space-y-2 sm:space-y-2.5 mb-2 sm:mb-3">
          {currentExample.steps.map((step: string, index: number) => {
            const isLast = index === currentExample.steps.length - 1;
            const stepStyle = STEP_CONFIG[index] || STEP_CONFIG[0];
            return (
              <div key={index} className="flex items-start gap-2 sm:gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-2 h-2 rounded-full ${stepStyle.color} mt-1.5`} />
                  {!isLast && <div className="w-px h-4 sm:h-5 bg-neutral-200 mt-1" />}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide">
                    {stepStyle.label}
                  </span>
                  <p className="text-xs sm:text-[13px] text-neutral-700 mt-0.5 leading-snug">{step}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div
          key={`footer-${currentIndex}`}
          className="flex items-center justify-between pt-2 sm:pt-2.5 border-t border-neutral-100"
        >
          <p className="text-[10px] sm:text-[11px] text-neutral-500">{currentExample.outcome}</p>
          <span className="text-[10px] sm:text-[11px] font-medium text-neutral-400 group-hover:text-primary-600 transition-colors">
            Try this →
          </span>
        </div>
      </Card>

      <CarouselDots
        total={examples.length}
        current={currentIndex}
        onDotClick={setCurrentIndex}
        autoRotateInterval={autoRotateInterval}
      />

      <style>{`
        @keyframes progressShrink {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

interface CarouselDotsProps {
  total: number;
  current: number;
  onDotClick: (index: number) => void;
  autoRotateInterval: number;
}

const CarouselDots: React.FC<CarouselDotsProps> = ({
  total,
  current,
  onDotClick,
  autoRotateInterval,
}) => {
  return (
    <div className="flex items-center justify-center gap-1.5 mt-3">
      {Array.from({ length: total }).map((_, index) => (
        <button
          key={index}
          onClick={() => onDotClick(index)}
          className={`h-1 rounded-full transition-all relative overflow-hidden ${
            index === current ? 'w-6 bg-neutral-200' : 'w-1 bg-neutral-300 hover:bg-neutral-400'
          }`}
          aria-label={`Go to example ${index + 1}`}
        >
          {index === current && (
            <span
              key={`progress-${current}`}
              className="absolute inset-0 bg-neutral-500 rounded-full origin-left"
              style={{ animation: `progressShrink ${autoRotateInterval}ms linear forwards` }}
            />
          )}
        </button>
      ))}
    </div>
  );
};
